import * as Sentry from '@sentry/react'

export enum TransactionName {
	DashboardInitialization = 'Dashboard - Initialization',
	DashboardLoadAllCampaignData = 'Dashboard - Load statistics data for all campaigns',
	DashboardLoadOneCampaign = 'Dashboard - Load specific campaign',
	DashboardCalculation = 'Dashboard - Client-side blocking calculation',
}

// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __VERSION__: string | undefined
declare const process: {
	env: {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		NODE_ENV?: string
	}
}

const env = process.env.NODE_ENV ?? 'test'
const isDevelopment = env === 'development'
const hasSentryDebug = () => location?.href.includes('sentryDebug')
const debug = isDevelopment && hasSentryDebug()
const sentryEnv = () => {
	if (location.hostname === 'localhost') {
		return 'development'
	} else if (location.hostname.includes('staging')) {
		return 'staging'
	} else {
		return 'production'
	}
}
// test (when running with jest) initializes Sentry with sentry-testkit transport in __shim__/shim.js
// and it apparently messes up fetch (Sentry-initialization probably rewrites fetch), so jest-fetch-mock
// will not work if tests are run with Sentry initialized here
export const initSentry = (): void => {
	if (env !== 'test') {
		Sentry.init({
			dsn: 'https://c0a2a7e40ade35d3610885cfb74f0a88@o4508369443291136.ingest.de.sentry.io/4508369562959952',
			release: `nativeflow:${__VERSION__ ?? 'unknown_version'}`,
			integrations: [
				Sentry.replayIntegration({
					maskAllText: true,
					blockAllMedia: true,
				}),
				Sentry.browserTracingIntegration(),
				Sentry.browserProfilingIntegration(),
			],
			tracePropagationTargets: ['localhost', 'https://api-staging.readpeak.com', 'https://api.readpeak.com'],

			environment: sentryEnv(),
			tracesSampleRate: 1.0,
			replaysSessionSampleRate: hasSentryDebug() ? 1 : 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
			replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
			profilesSampleRate: hasSentryDebug() ? 1 : 0.01,
			debug,
		})
	}
}

const setSentryUserContext = (email: string): void => {
	Sentry.setUser({ email } as Sentry.User)
}

const captureExceptionWithSentry = (error: unknown, hint?: Record<string, string | number>): string => {
	return Sentry.captureException(error, hint)
}

const captureMessageWithSentry = (message: string): void => {
	Sentry.captureMessage(message)
}

const spans = new Map<TransactionName, Sentry.Span>()

const startSentryTransaction = (name: TransactionName, meta?: Record<string, string>): void => {
	Sentry.startSpanManual(
		{
			op: 'react',
			name,
			attributes: meta ?? {},
		},
		(span) => spans.set(name, span),
	)
}

const endSentryTransaction = (name: TransactionName): void => {
	const span = spans.get(name)
	if (span) {
		span.end()
		spans.delete(name)
	}
}

export {
	setSentryUserContext,
	captureExceptionWithSentry,
	captureMessageWithSentry,
	startSentryTransaction,
	endSentryTransaction,
}
